/**
 * Team Selection Provider
 *
 * Holds the user's active team selection for "demo" orgs whose
 * `Organization.teamSelectionMode` is `linked` or `all`. Real customer orgs
 * default to `hidden` and run as if no team is active.
 *
 * The provider:
 * - Reads `mode` from `me.organization.teamSelectionMode` via CurrentUserProvider
 * - Persists `activeTeamId` per-user in AsyncStorage so it survives reloads
 * - Exposes `switchTeam(id)` which persists, publishes the new POV to
 *   `povTeamHeaderState` BEFORE calling `client.resetStore()` so the
 *   refetches that reset fires carry the new team's `x-pov-team-id`, and
 *   fires a single `logTeamSwitched` analytics event
 * - Exposes `clearTeam()` for the errorLink to invoke on `INVALID_ACTIVE_TEAM`
 * @module providers/TeamSelectionProvider
 */
import { useApolloClient } from "@apollo/client";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { AnalyticsTracker } from "@/features/analytics";
import { resetTargetsRulesState } from "@/features/financial-compliance/stores/targetsRulesStore";
import type { TeamSelectionMode } from "@/generated/graphql";
import { setPovTeamHeaderState } from "@/providers/povTeamHeaderState";
import { registerTeamSelectionRecovery } from "@/providers/teamSelectionErrorHandler";

// Backend SDL exposes lowercase enum values (`hidden` / `linked` / `all`).
// Keeping the values inline avoids requiring tests that mock
// `@/generated/graphql` to stub the runtime enum just for this Provider.
const HIDDEN_MODE = "hidden" as TeamSelectionMode;

const STORAGE_KEY_PREFIX = "@gemini-sports-ai:active-team";

/**
 * Per-(user, org) AsyncStorage key. Scoping to identity prevents a stale
 * selection from leaking across logouts and org switches — both of which
 * would otherwise reuse the previous account's last active team because
 * the storage value outlives the React tree.
 * @param userId - Current user id; required for a real key
 * @param organizationId - Current organization id (or `null` between sessions)
 * @returns The namespaced storage key for this identity
 */
const getStorageKey = (userId: string, organizationId: string | null): string =>
  `${STORAGE_KEY_PREFIX}:${userId}:${organizationId ?? "no-org"}`;

/**
 *
 */
interface PersistedTeam {
  readonly id: string;
  readonly name: string;
}

/**
 * Persisted selection paired with the identity key it was hydrated under.
 * Pairing the two lets render derive "has this identity finished hydrating?"
 * from a key match (`stored.key === storageKey`) instead of a lagging
 * `isLoaded` flag set in an effect — see `isHydrated` in the component.
 */
interface StoredState {
  /** Storage key the `team` was hydrated from, or null before the first load. */
  readonly key: string | null;
  /** Persisted team for `key`, or null when none is stored. */
  readonly team: PersistedTeam | null;
}

/**
 * Parses a raw AsyncStorage value into a {@link PersistedTeam}. Returns null
 * for absent, malformed, or pre-`{id,name}`-shape values.
 * @param raw - Raw string read from AsyncStorage, or null when nothing stored
 * @returns The parsed team, or null when the value can't be used
 */
const parseStoredTeam = (raw: string | null): PersistedTeam | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PersistedTeam;
    if (
      parsed &&
      typeof parsed.id === "string" &&
      typeof parsed.name === "string"
    ) {
      return parsed;
    }
  } catch {
    // Stored value pre-dates the {id, name} shape; ignore.
  }
  return null;
};

/**
 *
 */
export interface TeamSelectionContextValue {
  /** Active org's team-selection mode. Defaults to `hidden` while user data loads. */
  mode: TeamSelectionMode;
  /** Currently selected team UUID. `null` means "no team / org-wide view". */
  activeTeamId: string | null;
  /** Currently selected team display name. `null` when no team is active. */
  activeTeamName: string | null;
  /**
   * True once the `me` query has resolved AND this identity's persisted
   * selection has hydrated from AsyncStorage. Stays false through the
   * hard-refresh window where the current user is still unknown, so
   * consumers (and `povTeamHeaderLink`) never act on a placeholder POV.
   */
  isLoaded: boolean;
  /**
   * Switch the active team. Persists, resets the Apollo store so
   * team-sensitive queries refetch, and fires a single `team_switched` event.
   * The picker passes both id + name so the trigger label can render
   * without an extra round-trip in `all` mode (where we have no curated
   * list to look up names from).
   */
  switchTeam: (teamId: string, teamName: string) => Promise<void>;
  /** Clear the active team without firing analytics. Used by errorLink recovery. */
  clearTeam: () => Promise<void>;
}

const TeamSelectionContext = createContext<TeamSelectionContextValue>({
  mode: HIDDEN_MODE,
  activeTeamId: null,
  activeTeamName: null,
  isLoaded: false,
  switchTeam: async () => {},
  clearTeam: async () => {},
});

export const useTeamSelection = (): TeamSelectionContextValue =>
  useContext(TeamSelectionContext);

/**
 *
 */
interface Props {
  children: React.ReactNode;
}

export /**
 *
 */
const TeamSelectionProvider = ({ children }: Props) => {
  const currentUser = useCurrentUser();
  const apolloClient = useApolloClient();

  const mode = currentUser?.organization?.teamSelectionMode ?? HIDDEN_MODE;
  const userId = currentUser?.id ?? null;
  const organizationId = currentUser?.organizationId ?? null;

  const storageKey = useMemo(
    () => (userId ? getStorageKey(userId, organizationId) : null),
    [userId, organizationId]
  );

  const [stored, setStored] = useState<StoredState>({ key: null, team: null });

  // Coalesce concurrent `clearTeam` calls. Apollo can surface the same
  // INVALID_ACTIVE_TEAM error from several active queries before the
  // Provider rerenders; without this guard each fan-out call would
  // remove storage, resetStore, and fire `team_switched` again for what
  // is logically a single recovery action.
  const clearTeamInFlightRef = useRef<Promise<void> | null>(null);

  // `stored.team` is only valid for the identity it was hydrated under. On a
  // hard refresh `storageKey` flips null→real the instant the `me` query
  // resolves — a full commit before the async re-hydration refills `stored`.
  // Deriving "hydrated?" from the key match here (rather than from an
  // `isLoaded` state set in an effect) means the provider reports "not ready"
  // synchronously on that flip, so `povTeamHeaderLink` defers the first home
  // queries instead of forwarding them header-less (SE-4982).
  const isHydrated = stored.key === storageKey;

  // The registry behind `povTeamHeaderLink` must not be told "ready" until
  // the `me` query has resolved. While loading, `useCurrentUser()` returns
  // `undefined`, the org's real `mode` is unknown, and the placeholder
  // `hidden` would let a header-less request through. A signed-out user
  // resolves to `null`, not `undefined`, so logout still counts as ready.
  const isUserResolved = currentUser !== undefined;
  const isLoaded = isUserResolved && isHydrated;

  // Hidden-mode orgs cannot have an active team. Derive the exposed value so
  // a stale persisted id from a prior demo session never leaks into queries.
  // `stored.team` is gated on `isHydrated` so a team from a prior identity
  // never surfaces during the cross-identity hydration window.
  const storedTeam = isHydrated ? stored.team : null;
  const activeTeamId = mode === HIDDEN_MODE ? null : (storedTeam?.id ?? null);
  const activeTeamName =
    mode === HIDDEN_MODE ? null : (storedTeam?.name ?? null);

  const switchTeam = useCallback(
    async (teamId: string, teamName: string) => {
      const fromTeamId = activeTeamId;
      const next: PersistedTeam = { id: teamId, name: teamName };
      setStored({ key: storageKey, team: next });

      // Publish the new POV to the header registry HERE, not only from the
      // effect below. `resetStore()` empties the cache and refetches every
      // active query, and `povTeamHeaderLink` stamps `x-pov-team-id` by
      // reading this registry synchronously as each of those operations goes
      // out. The effect is a PASSIVE effect: it cannot run until React has
      // rendered the `setStored` above, and the `await` below yields only a
      // microtask — so the reset always won the race on web, and sometimes on
      // native. Every refetch then carried the PREVIOUS team, and because the
      // reset had just emptied the cache those previous-team answers were
      // written back in as the new team's with nothing left to refetch them.
      // The switch looked like it had done nothing (SE-8471).
      //
      // The value published is derived exactly as `activeTeamId` is in render
      // above, hidden-mode guard included, so this push and the effect's
      // cannot disagree.
      setPovTeamHeaderState({
        mode,
        activeTeamId: mode === HIDDEN_MODE ? null : teamId,
        isLoaded,
      });

      if (storageKey) {
        try {
          await AsyncStorage.setItem(storageKey, JSON.stringify(next));
        } catch (error) {
          console.warn("Failed to persist active team:", error);
        }
      }

      // SE-8453. Cleared HERE, before the reset, for the same reason the POV
      // is published above: `resetStore()` empties the cache and refetches
      // every active query immediately, and these variables are read while
      // those answers land. Clearing after the await leaves a window in which
      // a refetch resolves against the PREVIOUS club's figures.
      //
      // `resetStore()` does not do this itself and cannot: reactive variables
      // are independent of the cache, and the reset bottoms out in
      // `cache.reset({discardWatches: false})`, which re-inits storage and
      // touches no `makeVar`. Nothing else clears them either —
      // `resetTargetsRulesState` had no production caller at all before this,
      // despite its doc naming sign-out.
      //
      // All four of its variables are per-club and none is keyed by club: the
      // two adjusted-revenue variables are bare scalars, and the two override
      // maps are keyed by RULE ID, which are stable slugs deliberately shared
      // across clubs. `pendingAdjustedRevenueVar` is the one that reaches a
      // user, because it OVERRIDES the real read rather than merely backing
      // it up — so without this the next club is shown the previous club's
      // adjusted revenue even when it has a figure of its own.
      resetTargetsRulesState();

      if (apolloClient) {
        try {
          await apolloClient.resetStore();
        } catch (error) {
          console.warn("Failed to reset Apollo store on team switch:", error);
        }
      }

      AnalyticsTracker.user.logTeamSwitched({
        user_id: currentUser?.id ?? "",
        organization_id: currentUser?.organizationId ?? "",
        mode,
        from_team_id: fromTeamId,
        to_team_id: teamId,
        timestamp: new Date().toISOString(),
      });
    },
    [
      activeTeamId,
      apolloClient,
      currentUser?.id,
      currentUser?.organizationId,
      isLoaded,
      mode,
      storageKey,
    ]
  );

  const clearTeam = useCallback(async (): Promise<void> => {
    if (clearTeamInFlightRef.current) {
      return clearTeamInFlightRef.current;
    }

    const run = async () => {
      const fromTeamId = activeTeamId;
      setStored({ key: storageKey, team: null });

      // Same ordering contract as `switchTeam`, and it matters more here:
      // this is the errorLink's INVALID_ACTIVE_TEAM recovery, so refetching
      // under the team the backend has just rejected runs straight back into
      // the error being recovered from (SE-8471).
      setPovTeamHeaderState({ mode, activeTeamId: null, isLoaded });

      if (storageKey) {
        try {
          await AsyncStorage.removeItem(storageKey);
        } catch (error) {
          console.warn("Failed to clear active team:", error);
        }
      }

      // SE-8453, same reason and same position as in `switchTeam` above: the
      // reset refetches every active query, and these variables are read while
      // those answers land, so the clear has to precede the await rather than
      // follow it.
      //
      // Reached by BOTH of this provider's scope changes, because both change
      // which club is being acted within. This one is also the errorLink's
      // INVALID_ACTIVE_TEAM recovery, where leaving the departing club's
      // figures standing is worst: the team has just been rejected by the
      // backend and the reader is dropped into the no-team scope still looking
      // at its adjusted revenue.
      resetTargetsRulesState();

      if (apolloClient) {
        try {
          await apolloClient.resetStore();
        } catch (error) {
          console.warn("Failed to reset Apollo store on team clear:", error);
        }
      }

      // Fire the same analytics event with `to_team_id: null` so a clear
      // shows up alongside switches in the funnel. No-op when nothing was
      // active (errorLink recovery in particular often hits this path).
      if (fromTeamId !== null) {
        AnalyticsTracker.user.logTeamSwitched({
          user_id: currentUser?.id ?? "",
          organization_id: currentUser?.organizationId ?? "",
          mode,
          from_team_id: fromTeamId,
          to_team_id: null,
          timestamp: new Date().toISOString(),
        });
      }
    };

    clearTeamInFlightRef.current = run();
    try {
      await clearTeamInFlightRef.current;
    } finally {
      clearTeamInFlightRef.current = null;
    }
  }, [
    activeTeamId,
    apolloClient,
    currentUser?.id,
    currentUser?.organizationId,
    isLoaded,
    mode,
    storageKey,
  ]);

  const value = useMemo<TeamSelectionContextValue>(
    () => ({
      mode,
      activeTeamId,
      activeTeamName,
      isLoaded,
      switchTeam,
      clearTeam,
    }),
    [mode, activeTeamId, activeTeamName, isLoaded, switchTeam, clearTeam]
  );

  // Re-hydrate from storage whenever identity changes. A logout/login or
  // org switch changes `storageKey`, so the prior account's selection is
  // dropped here and the new account's (if any) is loaded fresh. Every
  // `setStored` call below stamps the `storageKey` it loaded under, so a
  // slow `getItem` resolving after the identity has moved on writes a
  // now-stale key that `isHydrated` ignores — the `isCurrent` flag is a
  // belt-and-braces guard on top of that.
  useEffect(() => {
    // eslint-disable-next-line functional/no-let -- ref-style flag captured by the async closures below
    let isCurrent = true;

    if (!storageKey) {
      // No identity (signed out, or `me` still loading). Drop any team from
      // a prior identity; `isHydrated` derives true once this lands because
      // both `stored.key` and `storageKey` are null.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- identity-driven reset
      setStored({ key: null, team: null });
      return () => {
        isCurrent = false;
      };
    }

    AsyncStorage.getItem(storageKey)
      .then(raw => {
        if (isCurrent) {
          setStored({ key: storageKey, team: parseStoredTeam(raw) });
        }
      })
      .catch(error => {
        if (!isCurrent) return;
        console.warn("Failed to load active team:", error);
        // Mark this identity hydrated-with-nothing so a failed read doesn't
        // leave the provider stuck reporting "not ready" forever.
        setStored({ key: storageKey, team: null });
      });

    return () => {
      isCurrent = false;
    };
  }, [storageKey]);

  useEffect(
    () =>
      registerTeamSelectionRecovery({
        clearTeam,
        getMode: () => mode,
        getActiveTeamId: () => activeTeamId,
      }),
    [clearTeam, mode, activeTeamId]
  );

  // Sync the current snapshot into the module-scoped registry that
  // `povTeamHeaderLink` reads on each outgoing operation. The link can't
  // depend on this provider via React context because it's constructed
  // inside `ApolloClientProvider`, which sits ABOVE us in the tree.
  //
  // This effect RECONCILES; it is no longer the only writer. Every path that
  // changes the POV and then immediately drives network traffic —
  // `switchTeam`, `clearTeam` — publishes the new value itself first, because
  // a passive effect runs too late to be read by the refetches those paths
  // trigger (SE-8471). Re-publishing the same snapshot here is a no-op beyond
  // flushing any link operations waiting on readiness.
  useEffect(() => {
    setPovTeamHeaderState({ mode, activeTeamId, isLoaded });
  }, [mode, activeTeamId, isLoaded]);

  return (
    <TeamSelectionContext.Provider value={value}>
      {children}
    </TeamSelectionContext.Provider>
  );
};

export { TeamSelectionContext };
